import { useState } from 'preact/hooks';
import { api } from '../../api/client';
import { Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { signOut } from '../../auth/useMe';
import { useTranslation } from '../../i18n';

const DELETE_CONFIRM_PHRASE = 'DELETE-MY-ACCOUNT';

/**
 * Data section of /me/account. GDPR Art. 15 + Art. 17 self-service
 * per ADR-0015 Phase 3.2.
 *
 *   • Export - direct link to /api/me/account/export. The backend
 *     streams one JSON file inline; the browser handles the download
 *     via Content-Disposition. No SMTP, no job queue.
 *   • Delete - typed-confirm against DELETE-MY-ACCOUNT. Soft-delete
 *     fires immediately (PII blanked, cascades wiped); the
 *     AccountHardDeleteSweep finishes the job after the 30-day grace.
 *     On success the SPA signs the user out so the next request
 *     observes the deleted state.
 */
export function AccountDataPage() {
    const t = useTranslation();
    const toast = useToast();
    const [confirmInput, setConfirmInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState<{
        deletedAt: string;
        hardDeleteEligibleAt: string;
    } | null>(null);

    const onDelete = async () => {
        if (confirmInput !== DELETE_CONFIRM_PHRASE) return;
        setBusy(true);
        try {
            const res = await api.me.data.delete(confirmInput);
            setDone({
                deletedAt: res.deletedAt,
                hardDeleteEligibleAt: res.hardDeleteEligibleAt,
            });
            toast.push(
                'success',
                <>
                    {t('me.data.delete.softDeleted')}
                </>,
            );
            // Give the toast a beat to render, then bounce out.
            setTimeout(() => signOut('/'), 1500);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', t('me.data.delete.error', { msg }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AccountShell
            title={t('me.data.title')}
            subtitle={t('me.data.subtitle')}
        >
            <div class="mx-auto w-full max-w-2xl space-y-12 px-[clamp(20px,3.5vw,56px)] py-10">
                <section>
                    <Eyebrow class="mb-3">{t('me.data.export.eyebrow')}</Eyebrow>
                    <p class="mb-3 text-sm text-fg-muted">
                        {t('me.data.export.body')}
                    </p>
                    <a
                        href={api.me.data.exportUrl}
                        class="inline-flex items-center gap-2 rounded border border-accent bg-accent px-4 py-2 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary"
                    >
                        {t('me.data.export.cta')}
                    </a>
                </section>

                <section>
                    <Eyebrow class="mb-3">{t('me.data.delete.eyebrow')}</Eyebrow>
                    {done ? (
                        <div class="rounded border border-state-failed/40 bg-state-failed/10 p-4">
                            <p class="text-sm text-fg-primary">
                                {t('me.data.delete.donePrefix')}{' '}
                                <time>
                                    {new Date(done.deletedAt).toLocaleString()}
                                </time>
                                {'. '}{t('me.data.delete.hardDeletePrefix')}{' '}
                                <time>
                                    {new Date(
                                        done.hardDeleteEligibleAt,
                                    ).toLocaleString()}
                                </time>
                                {'. '}{t('me.data.delete.signingOut')}
                            </p>
                        </div>
                    ) : (
                        <>
                            <p class="mb-3 text-sm text-fg-muted">
                                {t('me.data.delete.body')}
                            </p>
                            <p class="mb-3 text-sm text-fg-muted">
                                {t('me.data.delete.reregistration')}
                            </p>
                            <label class="block">
                                <span class="block text-sm text-fg-primary">
                                    {t('me.data.delete.typePromptPrefix')}{' '}
                                    <code class="font-mono">
                                        {DELETE_CONFIRM_PHRASE}
                                    </code>{' '}
                                    {t('me.data.delete.typePromptSuffix')}
                                </span>
                                <input
                                    type="text"
                                    value={confirmInput}
                                    onInput={(e) =>
                                        setConfirmInput(
                                            (e.target as HTMLInputElement).value,
                                        )
                                    }
                                    autoComplete="off"
                                    class="mt-1 w-full rounded border border-state-failed/40 bg-surface-raised px-3 py-1.5 font-mono text-sm text-fg-primary outline-none focus:border-state-failed"
                                />
                            </label>
                            <button
                                type="button"
                                onClick={onDelete}
                                disabled={
                                    busy || confirmInput !== DELETE_CONFIRM_PHRASE
                                }
                                class="mt-4 rounded border border-state-failed bg-state-failed/10 px-4 py-2 text-sm font-medium text-state-failed transition-colors hover:bg-state-failed hover:text-surface-base disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {busy ? t('me.data.delete.deleting') : t('me.data.delete.cta')}
                            </button>
                        </>
                    )}
                </section>
            </div>
        </AccountShell>
    );
}
